import { useCallback, useEffect, useState } from 'react';
// @mui
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import { DataGrid, GridColDef, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';
import { Chip, IconButton, InputAdornment, MenuItem, TextField } from '@mui/material';
import Typography from '@mui/material/Typography';
// routes
import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// utils
import { fDateTime } from 'src/utils/format-time';
// assets
// components
import { enqueueSnackbar } from 'src/components/snackbar';
import { ConfirmDialog } from 'src/components/custom-dialog';
import Image from 'src/components/image';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { useRouter } from 'src/routes/hook';
import { DateTime } from 'luxon';
import Iconify from 'src/components/iconify';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
// types
import { ITourFilterValue } from 'src/types/tour';
//
import EventFilters from '../event-filters';
import { useDispatch, useSelector } from '../../../redux/store';
import { getEvents } from '../../../redux/slices/event';
import { EntityStatus, EventDto, EventService } from '../../../api';
import { IEventFilters } from '../../../types/event';
import { useLocales } from '../../../locales';

// ----------------------------------------------------------------------

const defaultFilters: IEventFilters = {
  title: '',
  startDate: DateTime.now().minus({ month: 6 }),
  endDate: null,
};

// ----------------------------------------------------------------------

export default function EventListView() {
  const settings = useSettingsContext();

  const dispatch = useDispatch();

  const openFilters = useBoolean();

  const router = useRouter();

  const selectedGym = useSelector((state) => state.gym.globalGym);

  const { t } = useLocales();

  const confirm = useBoolean();

  const [search, setSearch] = useState<string>('');

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 5,
    page: 0,
  });

  const [queryOptions, setQueryOptions] = useState([
    { field: 'begins', sort: 'desc' },
  ] as GridSortModel);

  const handleSortModelChange = useCallback((sortModel: GridSortModel) => {
    setQueryOptions(sortModel);
  }, []);

  const [filters, setFilters] = useState(defaultFilters);

  const dateError =
    filters.startDate && filters.endDate ? filters.startDate > filters.endDate : false;

  const events = useSelector((state) => state.event.events);
  const totalNumber = useSelector((state) => state.event.totalEvents);
  const isLoading = useSelector((state) => state.event.isLoading);

  useEffect(() => {
    dispatch(
      getEvents({
        pageNumber: paginationModel.page,
        pageSize: paginationModel.pageSize,
        from: filters.startDate,
        to: filters.endDate,
        sortBy: queryOptions[0]?.field,
        direction: queryOptions[0]?.sort,
        search,
      })
    );
  }, [dispatch, filters, selectedGym, paginationModel, queryOptions, search]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const storedSortBy = queryParams.get('sortBy');
    const storedSearch = queryParams.get('search');
    const storedStartDate = queryParams.get('startDate')
      ? decodeURIComponent(queryParams.get('startDate')!)
      : null;
    const storedEndDate = queryParams.get('endDate')
      ? decodeURIComponent(queryParams.get('endDate')!)
      : null;

    setQueryOptions([
      {
        field: storedSortBy ?? 'begins',
        sort: 'desc',
      },
    ]);
    setSearch(storedSearch ?? '');
    setFilters({
      title: '',
      startDate:
        storedStartDate !== null
          ? DateTime.fromISO(storedStartDate, {
              zone: 'local',
            })
          : defaultFilters.startDate,
      endDate:
        storedEndDate !== null
          ? DateTime.fromISO(storedEndDate, {
              zone: 'local',
            })
          : null,
    });
  }, []);

  useEffect(() => {
    const newurl = `${window.location.protocol}//${window.location.host}${
      window.location.pathname
    }?sortBy=${queryOptions[0]?.field}&direction=${queryOptions[0]?.sort}&search=${
      search ?? ''
    }&startDate=${
      filters.startDate
        ? encodeURIComponent(filters.startDate.toISO({ includeOffset: false })!)
        : ''
    }&endDate=${
      filters.endDate ? encodeURIComponent(filters.endDate.toISO({ includeOffset: false })!) : ''
    }`;
    window.history.replaceState({ path: newurl }, '', newurl);
  }, [filters, queryOptions, search]);

  const canReset = !!filters.title.length || (!!filters.startDate && !!filters.endDate);

  const handleFilters = useCallback((name: string, value: ITourFilterValue) => {
    setFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }, []);

  const handleSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(event.target.value);
    },
    [setSearch]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const popover = usePopover();

  const [selectedEvent, setSelectedEvent] = useState<EventDto | null>(null);

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>, row: EventDto) => {
    setSelectedEvent(row);
    popover.onOpen(event);
  };

  const rows = [...events];

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      field: 'title',
      headerName: t('Title'),
      sortable: true,
      display: 'flex',
      flex: 2,
      renderCell: (params: GridRenderCellParams<any, any>) => (
        <Stack
          direction="row"
          sx={{ display: 'flex', alignItems: 'center' }}
          onClick={() => router.push(paths.events.view(params.row.id!))}
        >
          {params.row?.images?.length > 0 && (
            <Image
              alt={`${params.row?.title}`}
              src={params.row?.images![0].publicUrl}
              sx={{ borderRadius: 1, height: 48, width: 48, mr: 1, mt: 1, mb: 1 }}
            />
          )}
          <Typography
            variant="body2"
            sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
          >
            {`${params.row?.title}`}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'openForSignUpAt',
      headerName: t('Open for sign up on'),
      flex: 1,
      display: 'flex',
      valueFormatter: (value) => fDateTime(value),
    },
    {
      field: 'closedForSignUpAt',
      headerName: t('Closed for sign up on'),
      flex: 1,
      display: 'flex',
      valueFormatter: (value) => (value ? fDateTime(value) : ''),
    },
    {
      field: 'begins',
      headerName: t('Begins'),
      flex: 1,
      display: 'flex',
      valueFormatter: (value) => (value ? fDateTime(value) : ''),
    },
    {
      field: 'ends',
      headerName: t('Ends'),
      flex: 1,
      display: 'flex',
      valueFormatter: (value) => (value ? fDateTime(value) : ''),
    },
    {
      field: 'numberOfParticipants',
      headerName: t('Number of participants'),
      flex: 1,
      display: 'flex',
    },
    {
      field: 'state',
      headerName: t('Status'),
      width: 100,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<any, any>) => (
        <Chip
          label={params.row.state === EntityStatus.Archived ? t('Event cancelled') : t('Active')}
          color={params.row.state === EntityStatus.Archived ? 'error' : 'success'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: t(''),
      sortable: false,
      disableColumnMenu: true,
      align: 'right',
      display: 'flex',
      renderCell: (params: GridRenderCellParams<any, any>) => (
        <Stack direction="row">
          <IconButton
            color="default"
            onClick={() => {
              setSelectedEvent(params.row);
              handleView(selectedEvent!.id!);
            }}
          >
            <Iconify icon="solar:eye-bold" />
          </IconButton>
          {params.row.state !== EntityStatus.Archived && (
            <IconButton
              color={popover.open ? 'inherit' : 'default'}
              onClick={(e) => handleOpenPopover(e, params.row)}
            >
              <Iconify icon="eva:more-vertical-fill" />
            </IconButton>
          )}
        </Stack>
      ),
    },
  ];

  const renderFilters = (
    <Stack
      spacing={3}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-end', sm: 'center' }}
      direction={{ xs: 'column', sm: 'row' }}
    >
      <TextField
        value={search}
        onChange={handleSearch}
        placeholder={t('Search...')}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
            </InputAdornment>
          ),
        }}
      />

      <Stack direction="row" spacing={1} flexShrink={0}>
        <EventFilters
          open={openFilters.value}
          onOpen={openFilters.onTrue}
          onClose={openFilters.onFalse}
          //
          filters={filters}
          onFilters={handleFilters}
          //
          canReset={canReset}
          onResetFilters={handleResetFilters}
          //
          dateError={dateError}
        />
      </Stack>
    </Stack>
  );

  const handleView = useCallback(
    (id: number) => {
      router.push(paths.events.view(id));
    },
    [router]
  );

  const handleEdit = useCallback(
    (id: number) => {
      router.push(paths.events.edit(id));
    },
    [router]
  );

  const handleDelete = useCallback(
    async (id: number) => {
      await EventService.cancel({
        id,
      });

      enqueueSnackbar(t('Event cancelled!'));
      router.push(paths.events.root);
      dispatch(getEvents());
    },
    [t, router, dispatch]
  );

  return (
    <Container maxWidth={settings.themeStretch ? false : 'xl'}>
      <CustomBreadcrumbs
        heading={t('Events')}
        links={[
          { name: `${t('Dashboard')}`, href: paths.dashboard.root },
          {
            name: `${t('Event')}`,
            href: paths.events.root,
          },
          { name: `${t('Overview')}` },
        ]}
        action={
          <Button
            component={RouterLink}
            href={paths.events.new}
            variant="contained"
            color="primary"
            startIcon={<Iconify icon="mingcute:add-line" />}
          >
            {t('New event')}
          </Button>
        }
        sx={{
          mb: { xs: 3, md: 5 },
        }}
      />

      <Stack
        spacing={2.5}
        sx={{
          mb: { xs: 3, md: 5 },
        }}
      >
        {renderFilters}
      </Stack>

      <DataGrid
        rows={rows}
        columns={columns}
        loading={isLoading}
        getRowHeight={() => 'auto'}
        initialState={{
          sorting: {
            sortModel: queryOptions,
          },
        }}
        paginationMode="server"
        onSortModelChange={handleSortModelChange}
        rowCount={totalNumber}
        pageSizeOptions={[5, 10, 25]}
        checkboxSelection={false}
        disableRowSelectionOnClick
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
      />

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
        sx={{ width: 140 }}
      >
        <MenuItem
          onClick={() => {
            popover.onClose();
            handleEdit(selectedEvent!.id!);
          }}
        >
          <Iconify icon="solar:pen-bold" />
          {t('Edit')}
        </MenuItem>

        <MenuItem
          onClick={() => {
            popover.onClose();
            confirm.onTrue();
          }}
          sx={{ color: 'error.main' }}
        >
          <Iconify icon="solar:trash-bin-trash-bold" />
          {t('Cancel event')}
        </MenuItem>
      </CustomPopover>

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title={t('Cancel event')}
        content={
          <>
            {t('Are you sure you want to cancel ')}
            <strong>{selectedEvent?.title}</strong>?
          </>
        }
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              handleDelete(selectedEvent!.id!);
              confirm.onFalse();
            }}
          >
            {t('Cancel event')}
          </Button>
        }
      />
    </Container>
  );
}
